import { useId, useMemo, useRef, useState } from "react";
import { HomeframeInput } from "@builtbyted/homeframe";
import {
  geoDistance,
  geoEqualEarth,
  geoGraticule10,
  geoOrthographic,
  geoPath,
} from "d3-geo";
import { feature } from "topojson-client";
import type { Topology, GeometryCollection } from "topojson-specification";
import world from "./data/world.json";
import { count, duration, num, str, type Row } from "./ui";

export const colors = [
  "#a899ff",
  "#5ce0d2",
  "#5faaff",
  "#edac67",
  "#e986bc",
  "#badc78",
  "#d6d6ed",
  "#8cd1ff",
  "#dfbeff",
  "#ff988b",
  "#9ababa",
  "#dfca98",
];
export interface Series {
  label: string;
  points: [number, number | null][];
}
export interface Location {
  latitude: number;
  longitude: number;
  label: string;
  streams: number;
  users: string[];
}
const compact = (n: number) =>
  Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
export function sumSeries(series: Series[], label: string): Series[] {
  const values = new Map<number, number | null>();
  for (const s of series)
    for (const [t, v] of s.points)
      values.set(
        t,
        v === null ? (values.get(t) ?? null) : (values.get(t) ?? 0) + v,
      );
  return values.size
    ? [{ label, points: [...values].sort((a, b) => a[0] - b[0]) }]
    : [];
}
export function LineChart({
  series,
  unit = "",
  scale = 1,
  label,
  loading = false,
}: {
  series: Series[];
  unit?: string;
  scale?: number;
  label: string;
  loading?: boolean;
}) {
  const id = useId().replaceAll(":", "");
  const [hidden, setHidden] = useState<string[]>([]);
  const [focus, setFocus] = useState<number | null>(null);
  const shown = series.filter((s) => !hidden.includes(s.label));
  const times = [
    ...new Set(series.flatMap((s) => s.points.map((p) => p[0]))),
  ].sort((a, b) => a - b);
  const from = times[0] ?? 0,
    to = times.at(-1) ?? 1;
  const max =
    Math.max(
      1,
      ...shown.flatMap((s) => s.points.map((p) => (p[1] ?? 0) / scale)),
    ) * 1.12;
  const x = (t: number) => 48 + ((t - from) / Math.max(1, to - from)) * 890;
  const y = (v: number) => 200 - (v / max) * 174;
  let latest = times.length - 1;
  while (
    latest > 0 &&
    !series.some((s) =>
      s.points.some(([t, v]) => t === times[latest] && v !== null),
    )
  )
    latest--;
  const index = Math.min(times.length - 1, focus ?? Math.max(0, latest));
  const selected = times[index];
  const format = (v: number) =>
    `${Number((v / scale).toFixed(1)).toLocaleString()}${unit ? " " + unit : ""}`;
  const path = (s: Series) => {
    let started = false;
    return s.points
      .map(([t, v]) => {
        if (v === null) {
          started = false;
          return "";
        }
        const cmd = started ? "L" : "M";
        started = true;
        return `${cmd}${x(t).toFixed(1)},${y(v / scale).toFixed(1)}`;
      })
      .join(" ");
  };
  const tick = (t: number) =>
    new Date(t * 1000).toLocaleString(
      undefined,
      to - from > 86400
        ? { month: "short", day: "numeric" }
        : { hour: "numeric", minute: "2-digit" },
    );
  if (!times.length)
    return (
      <div className="chart-empty" role="status">
        {loading ? "Loading chart…" : "No activity in this period."}
      </div>
    );
  return (
    <div className="line-chart">
      <svg
        viewBox="0 0 960 240"
        role="img"
        aria-label={label}
        onPointerMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setFocus(
            Math.max(
              0,
              Math.min(
                times.length - 1,
                Math.round(
                  ((((e.clientX - rect.left) / rect.width) * 960 - 48) / 890) *
                    (times.length - 1),
                ),
              ),
            ),
          );
        }}
        onPointerLeave={() => setFocus(null)}
      >
        <defs>
          {shown.map((s, i) => (
            <linearGradient
              id={`${id}-${i}`}
              key={s.label}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={colors[i % colors.length]}
                stopOpacity=".25"
              />
              <stop
                offset="100%"
                stopColor={colors[i % colors.length]}
                stopOpacity=".01"
              />
            </linearGradient>
          ))}
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((n) => (
          <g key={n}>
            <line
              x1="48"
              x2="938"
              y1={y(max * n)}
              y2={y(max * n)}
              className="grid-line"
            />
            <text x="38" y={y(max * n) + 4} textAnchor="end">
              {compact(max * n)}
            </text>
          </g>
        ))}
        {shown.map((s) => (
          <path
            key={s.label}
            d={path(s)}
            fill="none"
            stroke={colors[series.indexOf(s) % colors.length]}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        ))}
        {selected !== undefined && (
          <line
            x1={x(selected)}
            x2={x(selected)}
            y1="20"
            y2="200"
            stroke="#a899ff"
            opacity=".5"
            strokeDasharray="4 4"
          />
        )}
        {[0, 0.25, 0.5, 0.75, 1].map((n) => (
          <text
            key={n}
            x={48 + n * 890}
            y="230"
            textAnchor={n === 0 ? "start" : n === 1 ? "end" : "middle"}
          >
            {tick(from + n * (to - from))}
          </text>
        ))}
      </svg>
      <div className="chart-readout">
        <time>
          {selected
            ? new Date(selected * 1000).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })
            : "—"}
        </time>
        <div>
          {shown.map((s) => {
            const v = s.points.find((p) => p[0] === selected)?.[1];
            return (
              <span key={s.label}>
                <i
                  style={{
                    background: colors[series.indexOf(s) % colors.length],
                  }}
                />
                {s.label}
                <strong>{v == null ? "No sample" : format(v)}</strong>
              </span>
            );
          })}
        </div>
      </div>
      <details className="chart-controls">
        <summary>Explore chart</summary>
        <HomeframeInput
          type="range"
          aria-label={`${label} time sample`}
          min={0}
          max={Math.max(0, times.length - 1)}
          value={index}
          onChange={(e) => setFocus(Number(e.target.value))}
        />
        <div className="legend">
          {series.map((s, i) => (
            <button
              key={s.label}
              aria-pressed={!hidden.includes(s.label)}
              onClick={() =>
                setHidden(
                  hidden.includes(s.label)
                    ? hidden.filter((v) => v !== s.label)
                    : [...hidden, s.label],
                )
              }
            >
              <i style={{ background: colors[i % colors.length] }} />
              {s.label}
            </button>
          ))}
        </div>
      </details>
    </div>
  );
}
export function Bars({
  rows,
  metric = "plays",
  onSelect,
}: {
  rows: Row[];
  metric?: string;
  onSelect?: (label: string) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => num(r[metric])));
  return (
    <div className="bar-list">
      {rows.length ? (
        rows.slice(0, 8).map((r, i) => (
          <button
            className="bar-row"
            key={str(r.label)}
            disabled={!onSelect}
            onClick={() => onSelect?.(str(r.label))}
          >
            <span>
              <i style={{ background: colors[i % colors.length] }} />
              {str(r.label) || "Unknown"}
              <strong>
                {metric === "duration" ? duration(r[metric]) : count(r[metric])}
              </strong>
            </span>
            <span className="bar-track">
              <span
                style={{
                  width: `${(num(r[metric]) / max) * 100}%`,
                  background: colors[i % colors.length],
                }}
              />
            </span>
          </button>
        ))
      ) : (
        <p className="empty-note">No activity in this period.</p>
      )}
    </div>
  );
}
const topology = world as unknown as Topology<{
  countries: GeometryCollection;
  land: GeometryCollection;
}>;
const land = feature(topology, topology.objects.land);
const countries = feature(topology, topology.objects.countries);
export function WorldMap({
  locations,
  unlocated = 0,
}: {
  locations: Location[];
  unlocated?: number;
}) {
  const [mode, setMode] = useState("globe");
  const [rotation, setRotation] = useState<[number, number]>([95, -25]);
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState<Location | null>(null);
  const drag = useRef<[number, number, number, number] | null>(null);
  const projection = useMemo(
    () =>
      mode === "globe"
        ? geoOrthographic()
            .rotate(rotation)
            .scale(148 * zoom)
            .translate([250, 166])
            .clipAngle(90)
        : geoEqualEarth().fitExtent(
            [
              [12, 12],
              [488, 320],
            ],
            { type: "Sphere" },
          ),
    [mode, rotation, zoom],
  );
  const path = geoPath(projection);
  const center: [number, number] = [-rotation[0], -rotation[1]];
  const focus = (p: Location) => {
    setSelected(p);
    setRotation([-p.longitude, -p.latitude]);
  };
  return (
    <div className="world-map">
      <div className="map-toolbar">
        <div className="segmented">
          {["globe", "map"].map((m) => (
            <button
              key={m}
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
            >
              {m === "globe" ? "Globe" : "Map"}
            </button>
          ))}
        </div>
        {mode === "globe" && (
          <div className="map-buttons">
            <button
              aria-label="Rotate globe west"
              onClick={() => setRotation([rotation[0] - 30, rotation[1]])}
            >
              ←
            </button>
            <button
              aria-label="Rotate globe east"
              onClick={() => setRotation([rotation[0] + 30, rotation[1]])}
            >
              →
            </button>
            <button
              aria-label="Zoom globe in"
              disabled={zoom >= 1.5}
              onClick={() => setZoom(Math.min(1.5, zoom + 0.25))}
            >
              +
            </button>
            <button
              aria-label="Zoom globe out"
              disabled={zoom <= 0.75}
              onClick={() => setZoom(Math.max(0.75, zoom - 0.25))}
            >
              −
            </button>
          </div>
        )}
      </div>
      <svg
        className={mode === "globe" ? "globe-canvas" : "map-canvas"}
        viewBox="0 0 500 332"
        role="img"
        aria-label="Approximate playback locations. Choose a location below to explore."
        onPointerDown={(e) => {
          if (mode === "globe") {
            drag.current = [e.clientX, e.clientY, ...rotation];
            e.currentTarget.setPointerCapture(e.pointerId);
          }
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onPointerMove={(e) => {
          if (drag.current) {
            const [x, y, lon, lat] = drag.current;
            setRotation([
              lon + (e.clientX - x) * 0.4,
              Math.max(-80, Math.min(80, lat - (e.clientY - y) * 0.4)),
            ]);
          }
        }}
      >
        <path
          d={path({ type: "Sphere" }) || ""}
          fill="#101d32"
          stroke="#344568"
        />
        <path
          d={path(geoGraticule10()) || ""}
          fill="none"
          stroke="#273753"
          strokeWidth=".6"
        />
        <path
          d={path(land) || ""}
          fill="#283a53"
          stroke="#617190"
          strokeWidth=".5"
        />
        <path
          d={path(countries) || ""}
          fill="none"
          stroke="#536580"
          strokeWidth=".3"
        />
        {locations.map((p) => {
          if (
            mode === "globe" &&
            geoDistance(center, [p.longitude, p.latitude]) > Math.PI / 2
          )
            return null;
          const xy = projection([p.longitude, p.latitude]);
          if (!xy) return null;
          return (
            <g key={`${p.latitude}:${p.longitude}`}>
              <circle
                cx={xy[0]}
                cy={xy[1]}
                r={Math.min(15, 4 + Math.sqrt(p.streams))}
                fill="#5ce0d2"
                opacity=".17"
              />
              <circle
                cx={xy[0]}
                cy={xy[1]}
                r={Math.min(6, 2 + Math.sqrt(p.streams) / 2)}
                fill="#69ebdc"
                stroke="#d4fff9"
                strokeWidth=".6"
              >
                <title>
                  {p.label} · {p.streams} streams seen
                </title>
              </circle>
            </g>
          );
        })}
      </svg>
      <p className="map-caption">
        {selected ? (
          <>
            <strong>{selected.label.replaceAll("None - ", "")}</strong>
            <br />
            {selected.users.join(", ")} · {count(selected.streams)} streams seen
          </>
        ) : (
          <>
            {locations.length} locations · Drag to explore
            <br />
            Approximate IP locations, not precise addresses
          </>
        )}
      </p>
      <div className="location-list">
        {locations.slice(0, 8).map((p) => (
          <button
            key={`${p.latitude}:${p.longitude}`}
            aria-pressed={selected === p}
            onClick={() => focus(p)}
          >
            <span>{p.label.replaceAll("None - ", "")}</span>
            <strong>{count(p.streams)}</strong>
          </button>
        ))}
      </div>
      {unlocated > 0 && (
        <small className="empty-note">
          {count(unlocated)} streams with unknown locations omitted.
        </small>
      )}
      <small className="map-attribution">
        Map data: Natural Earth · No external map requests
      </small>
    </div>
  );
}
export function Heatmap({
  rows,
  metric = "plays",
}: {
  rows: Row[];
  metric?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => num(r[metric])));
  const [selected, setSelected] = useState<number | null>(null);
  const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const content = (cell: number) => {
    const r = rows.find((x) => num(x.cell) === cell);
    return `${names[Math.floor(cell / 24)]} ${cell % 24}:00 · ${metric === "duration" ? duration(r?.duration) : `${count(r?.plays)} plays`}`;
  };
  return (
    <div className="heatmap">
      <div className="heatmap-grid">
        <span />
        {Array.from({ length: 24 }, (_, i) => (
          <small key={i}>{i % 3 === 0 ? i : ""}</small>
        ))}
        {names.map((day, d) => (
          <div className="heatmap-week" key={day}>
            <small>{day}</small>
            {Array.from({ length: 24 }, (_, h) => {
              const cell = d * 24 + h,
                value = num(rows.find((x) => num(x.cell) === cell)?.[metric]);
              return (
                <button
                  key={h}
                  title={content(cell)}
                  aria-label={content(cell)}
                  onClick={() => setSelected(cell)}
                  style={{
                    background: `rgba(168,153,255,${0.07 + 0.88 * Math.sqrt(value / max)})`,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="heatmap-caption">
        <span>
          {selected === null
            ? "Select an hour to explore viewing habits"
            : content(selected)}
        </span>
        <span>
          Less <i /> More
        </span>
      </div>
    </div>
  );
}
